/* ============================================================================
   WARMBELT CO — ATELIER LAYER  ·  behaviour
   ---------------------------------------------------------------------------
   Vanilla, dependency-free. Everything here is progressive enhancement: if this
   file never loads, the store looks and works exactly as it did before.

   Design rules followed throughout:
   · No layout reads inside scroll handlers — scroll only sets a flag, all
     measurement happens inside one shared requestAnimationFrame.
   · All scroll / pointer listeners are passive.
   · Reveal attributes are written BY this script, so a browser without
     IntersectionObserver never ends up with content stuck at opacity 0.
   · Nothing binds to add-to-cart, variant or gallery behaviour — those belong
     to product-page.js and product-gallery.js and are left strictly alone.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__wbAtelier) return;
  window.__wbAtelier = true;

  var doc = document;
  var root = doc.documentElement;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  var hasIO = 'IntersectionObserver' in window;

  function isReduced() { return reduced.matches; }
  function on(el, ev, fn, opts) { if (el) el.addEventListener(ev, fn, opts || false); }
  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }
  function $all(sel, ctx) { return (ctx || doc).querySelectorAll(sel); }

  /* One rAF pump shared by every per-frame consumer on the page. */
  var frameJobs = [];
  var framePending = false;
  function requestFrame() {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(function () {
      framePending = false;
      for (var i = 0; i < frameJobs.length; i++) {
        try { frameJobs[i](); } catch (e) { /* one bad job must not stop the rest */ }
      }
    });
  }
  function addFrameJob(fn) { frameJobs.push(fn); }

  /* ==========================================================================
     1 · SCROLL STATE — header elevation
     ========================================================================== */
  (function scrollState() {
    var last = null;
    function measure() {
      var scrolled = (window.pageYOffset || root.scrollTop) > 24;
      if (scrolled !== last) {
        last = scrolled;
        doc.body.classList.toggle('wba-scrolled', scrolled);
      }
    }
    addFrameJob(measure);
    on(window, 'scroll', requestFrame, { passive: true });
    on(window, 'resize', requestFrame, { passive: true });
    measure();
  })();

  /* ==========================================================================
     2 · REVEAL ON SCROLL
     Only elements with no reveal mechanism of their own are tagged, so the
     theme's existing .reveal / .rise animations are never doubled up.
     ========================================================================== */
  (function reveals() {
    if (!hasIO || isReduced()) return;

    var TARGETS = [
      '.feature-card',
      '.wb-cart-line',
      '.wb-cart-summary',
      '.wb-empty',
      '.page-rich-body > h2',
      '.page-rich-body > h3',
      '.page-rich-body > p',
      '.page-rich-body > ul',
      '.page-rich-body > ol',
      '.page-rich-body > img',
      '.wb-404 > *',
      'footer .footer-col'
    ];

    var nodes = [];
    each($all(TARGETS.join(',')), function (el) {
      /* Never touch anything the theme already animates. */
      if (el.classList.contains('reveal') || el.classList.contains('rise')) return;
      if (el.closest('[data-whl], .cine')) return;
      nodes.push(el);
    });
    if (!nodes.length) return;

    nodes.forEach(function (el, i) {
      el.setAttribute('data-wba-reveal', '');
      /* Stagger only within a short run, so long lists never feel slow. */
      var d = (i % 6) * 70;
      if (d) el.style.setProperty('--wba-delay', d + 'ms');
    });

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (!en.isIntersecting) return;
        en.target.classList.add('wba-in');
        io.unobserve(en.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

    nodes.forEach(function (el) { io.observe(el); });

    /* Failsafe: if anything prevents the observer from firing, nothing stays
       invisible. Content availability always outranks the animation. */
    window.setTimeout(function () {
      nodes.forEach(function (el) { el.classList.add('wba-in'); });
    }, 3000);
  })();

  /* ==========================================================================
     3 · SECTION SEAMS
     Skipped on the cinematic homepage, which blends its own sections with a
     gradient tail and would gain nothing from a hairline.
     ========================================================================== */
  (function seams() {
    if (doc.querySelector('[data-whl]')) return;
    var sections = $all('[id^="shopify-section-"]');
    each(sections, function (el, i) {
      el.classList.add('wba-sec');
      if (i === 0) el.classList.add('wba-sec-first');
    });
  })();

  /* ==========================================================================
     4 · HERO POINTER PARALLAX
     Writes two normalised values (-1 … 1) as custom properties on the hero
     section. All the actual movement is expressed in CSS, so this stays a
     two-property write per frame and never touches layout.
     ========================================================================== */
  (function heroParallax() {
    var hero = doc.querySelector('[data-whl]');
    if (!hero || isReduced() || !finePointer.matches) return;
    if (window.innerWidth < 821) return;

    var tx = 0, ty = 0, cx = 0, cy = 0, active = false;

    function onMove(e) {
      var w = window.innerWidth || 1;
      var h = window.innerHeight || 1;
      tx = ((e.clientX / w) - 0.5) * 2;
      ty = ((e.clientY / h) - 0.5) * 2;
      active = true;
      requestFrame();
    }

    addFrameJob(function () {
      if (!active) return;
      /* Ease towards the pointer so the product glides rather than snaps. */
      cx += (tx - cx) * 0.08;
      cy += (ty - cy) * 0.08;
      if (Math.abs(tx - cx) < 0.001 && Math.abs(ty - cy) < 0.001) {
        cx = tx; cy = ty; active = false;
      } else {
        requestFrame();
      }
      hero.style.setProperty('--wba-hx', cx.toFixed(4));
      hero.style.setProperty('--wba-hy', cy.toFixed(4));
    });

    on(window, 'pointermove', onMove, { passive: true });
    on(doc, 'pointerleave', function () {
      tx = 0; ty = 0; active = true; requestFrame();
    }, { passive: true });
  })();

  /* ==========================================================================
     4b · HERO CLOSING CTA — dwell time
     Measured on the live preview: the hero is 3772px tall with a 674px sticky
     stage, giving a 3098px scroll range. The section fades its closing CTA
     ("Relief you can wear.") in between 95% and 99% of that range, but the
     stage unsticks at 100% — so after a 560vh cinematic build-up the payoff
     was fully opaque for roughly 31 pixels of scroll. Effectively unseen.

     The clean fix is one constant inside sections/wb-hero-cinema.liquid, but
     that file is 31KB and carries 29 hand-tuned polygon coordinate arrays
     driving the exploded view; rewriting it wholesale to change two digits is
     a poor trade. So instead we take ownership of just this one element.

     Swapping the node for a clone is what makes that safe: the section's
     script captured its own reference at init, so after the swap its
     per-frame writes land on a detached element and do nothing, and we become
     the sole writer. Everything else in that sequence is untouched.
     ========================================================================== */
  (function heroFinalCta() {
    var hero = doc.querySelector('[data-whl]');
    if (!hero || isReduced()) return;
    /* No .whl-js means the section stood down (reduced motion, or its init
       threw). Its static fallback hides this element entirely — leave it. */
    if (!hero.classList.contains('whl-js')) return;

    var original = hero.querySelector('[data-whl-final]');
    if (!original) return;

    var cta = original.cloneNode(true);
    original.replaceWith(cta);

    var FADE_IN = 0.91;   /* section used 0.95 */
    var FULL_AT = 0.95;   /* section used 0.99 */

    var total = 0;
    function measure() { total = hero.offsetHeight - window.innerHeight; }

    var last = -1;
    function frame() {
      if (total <= 0) return;
      var p = (-hero.getBoundingClientRect().top) / total;
      if (p < 0) p = 0; else if (p > 1) p = 1;

      var t = (p - FADE_IN) / (FULL_AT - FADE_IN);
      if (t < 0) t = 0; else if (t > 1) t = 1;
      var f = t * t * (3 - 2 * t);           /* smoothstep, matching the section */

      if (Math.abs(f - last) < 0.004) return;
      last = f;

      cta.style.opacity = f;
      cta.style.visibility = f <= 0 ? 'hidden' : 'visible';
      cta.style.transform = 'translate3d(0,' + (20 - 20 * f).toFixed(1) + 'px,0)';
      /* Only clickable once it is actually readable, so the buttons can never
         be hit while the CTA is invisible over the rest of the sequence. */
      cta.style.pointerEvents = f > 0.5 ? 'auto' : 'none';
    }

    addFrameJob(frame);
    on(window, 'scroll', requestFrame, { passive: true });
    on(window, 'resize', function () { measure(); requestFrame(); }, { passive: true });
    measure();
    frame();
  })();

  /* ==========================================================================
     5 · HEAT & MASSAGE MODES PANEL
     sections/wb-features.liquid renders six static rows carrying
     data-int="1|2|3" — three heat levels, three massage modes. This turns each
     column into a self-demonstrating control: one row lit at a time, cycling
     while the panel is on screen, pinned while the visitor is hovering or
     tabbing through it.

     Purely presentational: no ARIA roles are invented for rows that do not
     actually do anything, and every row's text and level stay readable whether
     or not it happens to be the lit one.
     ========================================================================== */
  (function modesPanel() {
    var panels = $all('[id^="ft-"]');
    if (!panels.length) return;

    each(panels, function (panel) {
      each(panel.querySelectorAll('.ft-col'), function (col) {
        var rows = col.querySelectorAll('.ft-row');
        if (rows.length < 2) return;

        /* Intensity meter — decorative, so hidden from assistive tech. The row
           already states its level in words. */
        each(rows, function (row) {
          if (row.querySelector('.wba-meter')) return;
          var m = doc.createElement('span');
          m.className = 'wba-meter';
          m.setAttribute('aria-hidden', 'true');
          m.innerHTML = '<i></i><i></i><i></i>';
          row.appendChild(m);
        });

        if (isReduced()) {
          each(rows, function (row) { row.classList.add('wba-active'); });
          return;
        }

        var idx = 0, timer = null, held = false, visible = false;

        function light(n) {
          each(rows, function (row, i) { row.classList.toggle('wba-active', i === n); });
        }
        function step() {
          if (held) return;
          idx = (idx + 1) % rows.length;
          light(idx);
        }
        function start() {
          if (timer || !visible) return;
          timer = window.setInterval(step, 2000);
        }
        function stop() {
          if (!timer) return;
          window.clearInterval(timer);
          timer = null;
        }

        light(0);

        each(rows, function (row, i) {
          on(row, 'pointerenter', function () { held = true; idx = i; light(i); });
          on(row, 'pointerleave', function () { held = false; });
        });
        on(col, 'focusin', function () { held = true; });
        on(col, 'focusout', function () { held = false; });

        if (hasIO) {
          new IntersectionObserver(function (entries) {
            entries.forEach(function (en) {
              visible = en.isIntersecting;
              if (visible) start(); else stop();
            });
          }, { threshold: 0.25 }).observe(col);
        } else {
          visible = true;
          start();
        }
      });
    });
  })();

  /* ==========================================================================
     6 · CART COUNT FEEDBACK
     A small confirmation that the add landed, without touching the cart logic
     that produced it.
     ========================================================================== */
  (function cartCount() {
    var el = doc.getElementById('cartCount');
    if (!el || isReduced() || !('MutationObserver' in window)) return;
    var prev = el.textContent;
    new MutationObserver(function () {
      if (el.textContent === prev) return;
      prev = el.textContent;
      el.classList.add('wba-pop');
      window.setTimeout(function () { el.classList.remove('wba-pop'); }, 260);
    }).observe(el, { childList: true, characterData: true, subtree: true });
  })();

  /* ==========================================================================
     7 · CHECKOUT LOADING STATE (cart page only)
     A plain form submit that navigates away — safe to decorate, and it removes
     the dead moment between the click and the checkout page appearing.
     ========================================================================== */
  (function checkoutBusy() {
    var btn = doc.querySelector('.wb-cart-form button[name="checkout"]');
    if (!btn) return;
    on(btn, 'click', function () {
      window.setTimeout(function () { btn.classList.add('wba-busy'); }, 0);
    });
  })();

  /* ==========================================================================
     8 · CURSOR HALO — desktop, fine pointer, motion allowed
     The native cursor is never hidden; this is a soft companion that swells
     over interactive targets. Removed entirely on touch or reduced motion.
     ========================================================================== */
  (function cursor() {
    if (isReduced() || !finePointer.matches) return;
    if (window.innerWidth < 1025) return;

    var el = doc.createElement('div');
    el.className = 'wba-cursor';
    el.setAttribute('aria-hidden', 'true');
    doc.body.appendChild(el);

    var x = -100, y = -100, cx = -100, cy = -100, live = false;
    var HOT = 'a, button, [role="button"], input, select, textarea, label, summary,' +
              '.feature-card, .pg-thumb, .pg-dot, .pb-swatch, .cine-card, .mcard, .lcard';

    on(window, 'pointermove', function (e) {
      x = e.clientX; y = e.clientY;
      if (!live) { live = true; cx = x; cy = y; el.classList.add('is-live'); }
      var t = e.target;
      el.classList.toggle('is-hot', !!(t && t.closest && t.closest(HOT)));
      requestFrame();
    }, { passive: true });

    on(window, 'pointerdown', function () { el.classList.add('is-down'); }, { passive: true });
    on(window, 'pointerup', function () { el.classList.remove('is-down'); }, { passive: true });
    on(doc, 'pointerleave', function () { el.classList.remove('is-live'); live = false; }, { passive: true });
    on(doc, 'pointerenter', function () { el.classList.add('is-live'); }, { passive: true });

    addFrameJob(function () {
      if (!live) return;
      cx += (x - cx) * 0.22;
      cy += (y - cy) * 0.22;
      el.style.transform = 'translate3d(' + cx.toFixed(1) + 'px,' + cy.toFixed(1) + 'px,0)';
      if (Math.abs(x - cx) > 0.4 || Math.abs(y - cy) > 0.4) requestFrame();
    });
  })();

  /* ==========================================================================
     9 · THEME EDITOR
     Shopify replaces a section's markup in place when it is edited, which drops
     the meters this script injected and the seam class it added. The modes
     panel owns its own cycling timers via IntersectionObserver, so only the
     injected DOM needs restoring — re-running the whole file would stack a
     second set of listeners on everything else.
     ========================================================================== */
  on(doc, 'shopify:section:load', function (e) {
    var scope = (e && e.target) || doc;

    each(scope.querySelectorAll ? scope.querySelectorAll('[id^="ft-"] .ft-row') : [], function (row) {
      if (row.querySelector('.wba-meter')) return;
      var m = doc.createElement('span');
      m.className = 'wba-meter';
      m.setAttribute('aria-hidden', 'true');
      m.innerHTML = '<i></i><i></i><i></i>';
      row.appendChild(m);
      if (isReduced()) row.classList.add('wba-active');
    });

    if (!doc.querySelector('[data-whl]') && scope.id && scope.id.indexOf('shopify-section-') === 0) {
      scope.classList.add('wba-sec');
    }
  });
})();
